// @dynamic-i18n-keys
// Dynamic t() at line 87 (value === '0' ? original : preset):
//   deviationSubmit.form.displayResolution.option.label.original
//   deviationSubmit.form.displayResolution.option.label.preset
import React, { useCallback, useContext, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useController, useFormContext } from 'react-hook-form';
import { PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import SelectField from '@wix/da-ds/pkg/Dropdown/select/SelectField';
import BooleanInput from '@wix/da-ds/pkg/formControls/dsPresets/BooleanInput';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import FormElement from '@wix/da-ds/pkg/formControls/layouts/FormElement';
import { getDeviationType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import {
  DeviationOriginalFile,
  getDeviationOriginalFile,
} from '@wix/da-shared-react/pkg/utils/deviations';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import MediaService from '@wix/da-shared-react/pkg/utils/DeviationMediaService';
import { MIN_SIZE_FOR_WATERMARK } from '../../../../fields/AddWatermarkField/AddWatermarkField';

import s from './DisplayOptionsField.scss';

/** Preset widths for display resolution indices '1'–'8' (matches AddWatermarkField / server display options). */
const PRESET_DISPLAY_WIDTHS = [400, 600, 800, 900, 1024, 1280, 1600, 1920] as const;

/**
 * When enabling watermark on Original, pick the highest preset that is still in the
 * dropdown (width <= fileWidth) and matches the watermark cap (width <= 1024).
 * Mirrors shared/da-submit-shared/src/fields/AddWatermarkField/AddWatermarkField.tsx
 */
function getDisplayResolutionIndexForWatermarkFromOriginal(fileWidth: number): string | undefined {
  let highestOption: string | undefined;
  for (let i = 0; i < PRESET_DISPLAY_WIDTHS.length; i++) {
    const w = PRESET_DISPLAY_WIDTHS[i];
    if (w > 1024) {
      break;
    }
    if (w <= fileWidth) {
      highestOption = `${i + 1}`;
    }
  }
  return highestOption;
}

interface Props {
  deviation?: PapiDeviation;
  displayResolutionFieldName: string;
  addWatermarkFieldName: string;
  label: string;
}

const DisplayOptionsField: React.FC<Props> = ({
  deviation,
  displayResolutionFieldName,
  addWatermarkFieldName,
  label,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const id = useId();
  const { control } = useFormContext();
  const { field: displayResolutionField } = useController({
    name: displayResolutionFieldName,
    control,
  });
  const { field: watermarkField } = useController({
    name: addWatermarkFieldName,
    control,
  });

  // Lifted from the standard submit form's DisplayOptionsForm:
  // Only show display options if the fullview is eligible
  const isFilm = getDeviationType(deviation) === DeviationTypes.FILM;
  const isGif = !!deviation && MediaService.isGif(deviation);
  const originalFile: DeviationOriginalFile = getDeviationOriginalFile(deviation);
  const { width: fileWidth = 0, height: fileHeight = 0 } = originalFile ?? {};
  const isTooSmallForDisplayOpts =
    fileWidth < MIN_SIZE_FOR_WATERMARK && fileHeight < MIN_SIZE_FOR_WATERMARK;
  const isOriginalResolutionSelected = `${displayResolutionField.value}` === '0';
  const isWatermarkDisabled = isTooSmallForDisplayOpts || isOriginalResolutionSelected;

  const handleWatermarkFieldChange = useCallback(
    (value: boolean) => {
      if (value && `${displayResolutionField.value}` === '0') {
        const nextIndex = getDisplayResolutionIndexForWatermarkFromOriginal(fileWidth);
        if (nextIndex !== undefined) {
          displayResolutionField.onChange(nextIndex);
        }
      }
      watermarkField.onChange(value);
    },
    [watermarkField, displayResolutionField, fileWidth]
  );

  const handleDisplayResolutionChange = useCallback(
    (newValue: string) => {
      if (`${newValue}` === '0') {
        // When orignal resolution is selected, we disable the watermark option
        // and uncheck it, since we don't want to allow watermarks on original
        // files.
        watermarkField.onChange(false);
      }
      displayResolutionField.onChange(newValue);
    },
    [watermarkField, displayResolutionField]
  );

  useEffect(() => {
    if (isWatermarkDisabled && watermarkField.value) {
      watermarkField.onChange(false);
    }
  }, [isWatermarkDisabled, watermarkField]);

  if (isFilm || isGif) {
    return null;
  }

  const options: Array<{ value: string; width: number; height?: number }> = [
    { value: '0', width: fileWidth, height: fileHeight },
    ...PRESET_DISPLAY_WIDTHS.map((width, idx) => ({
      value: `${idx + 1}`,
      width,
    })),
  ];

  return (
    <FormElement
      inputId={id}
      className={s['root']}
      label={<InputLabelText>{label}</InputLabelText>}
    >
      <div className={s['inputs']}>
        <SelectField
          id={id}
          className={s['display-resolution']}
          label={label}
          items={options
            .filter(item => item.width <= fileWidth)
            .map(({ value, width, height }) => ({
              value,
              label: t(
                `deviationSubmit.form.displayResolution.option.label.${
                  value === '0' ? 'original' : 'preset'
                }`,
                { width, height }
              ),
            }))}
          value={displayResolutionField.value}
          onSelect={handleDisplayResolutionChange}
          withBlankOption={false}
          isLabelHidden
        />
        <BooleanInput
          id={id}
          label={t('deviationSubmit.form.addWatermark.label')}
          onChange={handleWatermarkFieldChange}
          value={watermarkField.value}
          size={isMobile ? 'large' : 'small'}
          noBottomSpacing
          disabled={isWatermarkDisabled}
        />
      </div>
    </FormElement>
  );
};
DisplayOptionsField.displayName = 'DisplayOptionsField';

export default DisplayOptionsField;
